import type { SVGProps } from "react";

/* ============================================================
   Ícones

   São os mesmos traços do app anterior, portados um a um. A tela de
   publicar usava emoji no lugar deles e ficou parecendo protótipo: emoji
   muda de desenho em cada sistema, não herda a cor do tema e não alinha
   com texto. Ícone de traço herda `currentColor` e some no tema escuro
   junto com o resto.
   ============================================================ */

type Props = { size?: number } & Omit<SVGProps<SVGSVGElement>, "size">;

function Svg({ size = 24, children, ...rest }: Props) {
  return (
    <svg
      viewBox="0 0 24 24" width={size} height={size}
      fill="none" stroke="currentColor" strokeWidth={2}
      strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" focusable="false"
      {...rest}
    >
      {children}
    </svg>
  );
}

export const IconBack = (p: Props) => (
  <Svg {...p} strokeWidth={2.2}><path d="M15 5l-7 7 7 7" /></Svg>
);

export const IconClose = (p: Props) => (
  <Svg {...p} strokeWidth={2.2}><path d="M6 6l12 12M18 6L6 18" /></Svg>
);

export const IconCheck = (p: Props) => (
  <Svg {...p} strokeWidth={2.4}><path d="M4 12.5l5.2 5.2L20 7" /></Svg>
);

export const IconCheckSmall = (p: Props) => (
  <Svg {...p} strokeWidth={3}><path d="M5 12.5l4.4 4.4L19 7.5" /></Svg>
);

export const IconChevron = (p: Props) => (
  <Svg {...p}><path d="M9 5l7 7-7 7" /></Svg>
);

export const IconPencil = (p: Props) => (
  <Svg {...p} strokeWidth={1.9}><path d="M4 20h4L19.5 8.5a2.1 2.1 0 0 0-3-3L5 17v3z" /></Svg>
);

export const IconCamera = (p: Props) => (
  <Svg {...p} strokeWidth={1.8}>
    <path d="M4 8h3l1.6-2.4h6.8L17 8h3a1.5 1.5 0 0 1 1.5 1.5v9A1.5 1.5 0 0 1 20 20H4a1.5 1.5 0 0 1-1.5-1.5v-9A1.5 1.5 0 0 1 4 8z" />
    <circle cx="12" cy="13.5" r="3.6" />
  </Svg>
);

export const IconImage = (p: Props) => (
  <Svg {...p} strokeWidth={1.8}>
    <rect x="3" y="5" width="18" height="14" rx="2.2" />
    <circle cx="8.5" cy="10" r="1.6" />
    <path d="M4 17l4.8-4.5a1.6 1.6 0 0 1 2.2 0L16 17" />
    <path d="M14.5 14.8l1.6-1.5a1.6 1.6 0 0 1 2.2 0L21 16" />
  </Svg>
);

export const IconFlip = (p: Props) => (
  <Svg {...p} strokeWidth={1.9}>
    <path d="M20 11a8 8 0 0 0-13.7-5.3L4 8" /><path d="M4 4v4h4" />
    <path d="M4 13a8 8 0 0 0 13.7 5.3L20 16" /><path d="M20 20v-4h-4" />
  </Svg>
);

export const IconFlashOff = (p: Props) => (
  <Svg {...p} strokeWidth={1.9}>
    <path d="M13 2L5 13h5l-1 9 5-7" /><path d="M3 3l18 18" />
  </Svg>
);

export const IconCalendar = (p: Props) => (
  <Svg {...p} strokeWidth={1.8}>
    <rect x="3.5" y="5" width="17" height="16" rx="2.2" />
    <path d="M3.5 10h17M8 3v4M16 3v4" />
  </Svg>
);

export const IconClock = (p: Props) => (
  <Svg {...p} strokeWidth={1.9}>
    <circle cx="12" cy="12" r="9" /><path d="M12 7.5V12l3 1.8" />
  </Svg>
);

export const IconPin = (p: Props) => (
  <Svg {...p} strokeWidth={1.8}>
    <path d="M12 21s7-5.6 7-11a7 7 0 1 0-14 0c0 5.4 7 11 7 11z" />
    <circle cx="12" cy="10" r="2.6" />
  </Svg>
);

/* Mapa dobrado, não pino: o pino marca UM lugar e é o que a linha de
   "Onde foi?" já usa. A aba mostra TODOS, e as três dobras dizem isso
   sem precisar do rótulo. */
export const IconMap = (p: Props) => (
  <Svg {...p} strokeWidth={1.7}>
    <path d="M9 4.5 3.6 6.6v12.9L9 17.4l6 2.1 5.4-2.1V4.5l-5.4 2.1z" />
    <path d="M9 4.5v12.9M15 6.6v12.9" />
  </Svg>
);

export const IconTarget = (p: Props) => (
  <Svg {...p} strokeWidth={1.8}>
    <circle cx="12" cy="12" r="8.5" /><circle cx="12" cy="12" r="4.2" />
    <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
  </Svg>
);

export const IconDots = (p: Props) => (
  <Svg {...p}>
    <circle cx="5" cy="12" r="1.6" fill="currentColor" stroke="none" />
    <circle cx="12" cy="12" r="1.6" fill="currentColor" stroke="none" />
    <circle cx="19" cy="12" r="1.6" fill="currentColor" stroke="none" />
  </Svg>
);

export const IconMenu = (p: Props) => (
  <Svg {...p} strokeWidth={2.4}><path d="M3 7h13" /><path d="M3 13h7" /></Svg>
);

export const IconBell = (p: Props) => (
  <Svg {...p}>
    <path d="M18 8a6 6 0 1 0-12 0c0 6-2 7-2 7h16s-2-1-2-7" />
    <path d="M10.5 20a2 2 0 0 0 3 0" />
  </Svg>
);

export const IconPlus = (p: Props) => (
  <Svg {...p} strokeWidth={2.6}><path d="M12 5v14M5 12h14" /></Svg>
);

export const IconFork = (p: Props) => (
  <Svg {...p} strokeWidth={2}>
    <path d="M7 3v6a2.5 2.5 0 0 0 5 0V3" /><path d="M9.5 11v10" />
    <path d="M17.5 3c-1.6 1-2.5 3-2.5 5.5s.9 3.5 2.5 3.5V21" />
  </Svg>
);

/* ---------- abas ---------- */

/** O feed. Casa, e não prato, porque é a tela pra onde a pessoa volta. */
export const IconHome = (p: Props) => (
  <Svg {...p} strokeWidth={1.7}>
    <path d="M4 10.6 12 4l8 6.6V19a2 2 0 0 1-2 2h-3v-6H9v6H6a2 2 0 0 1-2-2z" />
  </Svg>
);

export const IconDetails = (p: Props) => (
  <Svg {...p} strokeWidth={1.7}>
    <rect x="3" y="4" width="18" height="16" rx="2.5" />
    <circle cx="8.5" cy="10" r="2" />
    <path d="M5.5 16.2c.6-1.5 1.7-2.2 3-2.2s2.4.7 3 2.2" />
    <path d="M15 9.5h4M15 13h4" />
  </Svg>
);

export const IconMedal = (p: Props) => (
  <Svg {...p} strokeWidth={1.7}>
    <circle cx="12" cy="9" r="5.2" />
    <path d="M9.4 13.6L8 21l4-2.2L16 21l-1.4-7.4" />
  </Svg>
);

export const IconChat = (p: Props) => (
  <Svg {...p} strokeWidth={1.7}>
    <path d="M21 11.5a8 8 0 0 1-8.5 8 9 9 0 0 1-3.4-.6L4 21l1.4-4.2A8 8 0 0 1 12.5 3.5 8 8 0 0 1 21 11.5z" />
  </Svg>
);

export const IconShare = (p: Props) => (
  <Svg {...p}>
    <path d="M12 16V4M12 4 8 8M12 4l4 4" />
    <path d="M5 14v4a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4" />
  </Svg>
);

export const IconFlag = (p: Props) => (
  <Svg {...p}>
    <path d="M5 21V4M5 4h12l-2.5 4L17 12H5" />
  </Svg>
);

export const IconBlock = (p: Props) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M6 6l12 12" />
  </Svg>
);

/* Sair do grupo: a moldura de porta e a seta saindo dela. É o glifo de
   "sair" que todo app usa, e desenhado com o mesmo traço dos outros — o
   emoji de porta que estava no lugar era o único adesivo colorido numa
   lista de ícones monocromáticos. */
export const IconSair = (p: Props) => (
  <Svg {...p}><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" /></Svg>
);

export const IconTrash = (p: Props) => (
  <Svg {...p} strokeWidth={1.8}>
    <path d="M4 7h16M9 7V5.5A1.5 1.5 0 0 1 10.5 4h3A1.5 1.5 0 0 1 15 5.5V7" />
    <path d="M6 7l1 12.5A1.5 1.5 0 0 0 8.5 21h7a1.5 1.5 0 0 0 1.5-1.5L18 7" />
  </Svg>
);

/** Nota. Contorno, não preenchida: no rodapé do feed ela conta, não premia. */
export const IconStar = (p: Props) => (
  <Svg {...p} strokeWidth={1.8}>
    <path d="M12 3.6l2.6 5.3 5.8.8-4.2 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8-4.2-4.1 5.8-.8z" />
  </Svg>
);

export const IconBolt = (p: Props) => (
  <Svg {...p} strokeWidth={1.8}><path d="M13 2L5 13h5l-1 9 8-11h-5l1-9z" /></Svg>
);
